import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, KeyRound, Delete } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { GoogleLoginButton } from './GoogleLoginButton';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { LanguageFlags } from '../components/LanguageFlags';
import { AIErrorHelper } from '../components/AIErrorHelper';

const TABS = { PASSWORD: 'password', PIN: 'pin' };

export const Login = () => {
  const navigate = useNavigate();
  const { login, pinLogin } = useAuth();
  const { t } = useTranslation();

  const [tab, setTab] = useState(TABS.PASSWORD);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [loading, setLoading] = useState(false);

  const switchTab = (newTab) => {
    setTab(newTab);
    setError('');
    setErrorCode('');
    setPin('');
  };

  const handlePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setErrorCode('');
    const result = await login(email, password, rememberMe);
    if (result.success) {
      navigate('/dashboard');
    } else {
      const raw = result.error;
      const code = typeof raw === 'string' ? raw : (raw?.detail || 'unknown');
      const key = `auth.errors.${code}`;
      const tx = t(key);
      setError(tx !== key ? tx : code);
      setErrorCode(typeof code === 'string' ? code : '');
    }
    setLoading(false);
  };

  const handlePin = async (fullPin) => {
    if (!email) {
      setError(t('auth.errors.email_required', 'Bitte Email eingeben'));
      return;
    }
    setLoading(true);
    setError('');
    setErrorCode('');
    const result = await pinLogin(email, fullPin);
    if (result.success) {
      navigate('/dashboard');
    } else {
      const code = result.error || 'invalid_credentials';
      const key = `auth.errors.${code}`;
      const tx = t(key);
      setError(tx !== key ? tx : code);
      setErrorCode(code);
      setPin('');
    }
    setLoading(false);
  };

  const pinPress = (digit) => {
    if (loading || pin.length >= 6) return;
    const next = pin + String(digit);
    setPin(next);
    setError('');
    if (next.length === 6) setTimeout(() => handlePin(next), 200);
  };

  const pinBack = () => setPin(pin.slice(0, -1));

  return (
    <div className="auth-container">
      <div className="auth-lang-switcher" data-testid="auth-lang-switcher">
        <LanguageSwitcher />
      </div>
      <div className="auth-card">
        <h1 className="auth-title" data-testid="login-title">{t('auth.login.title')}</h1>
        <p className="auth-subtitle">{t('auth.login.subtitle')}</p>

        <div style={{ marginBottom: 16 }}>
          <LanguageFlags />
        </div>

        {/* Tab Switcher: Password ↔ PIN */}
        <div style={tabStyles.row} data-testid="login-tabs">
          <button
            type="button"
            onClick={() => switchTab(TABS.PASSWORD)}
            data-testid="login-tab-password"
            style={{
              ...tabStyles.tab,
              ...(tab === TABS.PASSWORD ? tabStyles.tabActive : {}),
            }}
          >
            <Lock size={14} style={{ marginRight: 6 }} />
            {t('auth.login.tabPassword', 'Passwort')}
          </button>
          <button
            type="button"
            onClick={() => switchTab(TABS.PIN)}
            data-testid="login-tab-pin"
            style={{
              ...tabStyles.tab,
              ...(tab === TABS.PIN ? tabStyles.tabActive : {}),
            }}
          >
            <KeyRound size={14} style={{ marginRight: 6 }} />
            {t('auth.login.tabPin', 'PIN')}
          </button>
        </div>

        {tab === TABS.PASSWORD ? (
          <form onSubmit={handlePassword} className="auth-form">
            <div className="form-group">
              <label>{t('auth.login.email')}</label>
              <div className="input-wrapper">
                <Mail size={18} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="max@example.com"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t('auth.login.password')}</label>
              <div className="input-wrapper">
                <Lock size={18} />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.login.passwordPlaceholder')}
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <div className="checkbox-group">
              <input
                type="checkbox"
                id="remember"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <label htmlFor="remember">{t('auth.login.rememberMe')}</label>
            </div>

            {error && (
              <div className="error-box" data-testid="login-error">
                {error}
                {errorCode && <AIErrorHelper code={errorCode} testId="login-ai-helper" />}
              </div>
            )}

            <button type="submit" className="auth-button" disabled={loading} data-testid="login-submit-button">
              {loading ? t('auth.login.submitting') : t('auth.login.submit')}
            </button>
          </form>
        ) : (
          <div className="auth-form" data-testid="login-pin-mode">
            <div className="form-group">
              <label>{t('auth.login.email')}</label>
              <div className="input-wrapper">
                <Mail size={18} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="max@example.com"
                  required
                  data-testid="login-pin-email-input"
                />
              </div>
            </div>

            <div style={{ textAlign: 'center', margin: '12px 0 18px', fontSize: 13, color: '#64748b' }}>
              {t('auth.login.pinHint', '6-stellige PIN eingeben')}
            </div>

            {/* PIN dots */}
            <div style={pinStyles.dots} data-testid="login-pin-dots">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  style={{
                    ...pinStyles.dot,
                    background: i < pin.length ? '#0ea5e9' : '#e2e8f0',
                    transform: i < pin.length ? 'scale(1.1)' : 'scale(1)',
                  }}
                />
              ))}
            </div>

            {error && (
              <div className="error-box" data-testid="login-pin-error">
                {error}
                {errorCode && <AIErrorHelper code={errorCode} testId="login-pin-ai-helper" />}
              </div>
            )}

            <div style={pinStyles.pad}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => pinPress(n)}
                  disabled={loading}
                  data-testid={`login-pin-key-${n}`}
                  style={pinStyles.key}
                >
                  {n}
                </button>
              ))}
              <div style={{ ...pinStyles.key, ...pinStyles.keyEmpty }} />
              <button
                type="button"
                onClick={() => pinPress(0)}
                disabled={loading}
                data-testid="login-pin-key-0"
                style={pinStyles.key}
              >
                0
              </button>
              <button
                type="button"
                onClick={pinBack}
                disabled={loading || pin.length === 0}
                data-testid="login-pin-key-back"
                style={{ ...pinStyles.key, ...pinStyles.keyBack }}
              >
                <Delete size={18} />
              </button>
            </div>
          </div>
        )}

        <div style={{ margin: '1.5rem 0' }}>
          <div style={{ textAlign: 'center', margin: '1rem 0', color: '#94a3b8' }}>
            {t('auth.login.or')}
          </div>
          <GoogleLoginButton />
        </div>

        <p className="auth-footer">
          {t('auth.login.noAccount')} <Link to="/register" data-testid="register-link">{t('auth.login.register')}</Link>
        </p>
      </div>
    </div>
  );
};

const tabStyles = {
  row: {
    display: 'flex',
    gap: 8,
    marginBottom: 16,
    background: '#f1f5f9',
    padding: 4,
    borderRadius: 12,
  },
  tab: {
    flex: 1,
    padding: '10px 14px',
    borderRadius: 9,
    border: 'none',
    background: 'transparent',
    color: '#64748b',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s ease',
  },
  tabActive: {
    background: '#ffffff',
    color: '#0ea5e9',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
};

const pinStyles = {
  dots: { display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 18 },
  dot: { width: 14, height: 14, borderRadius: '50%', transition: 'all 0.2s ease' },
  pad: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 8 },
  key: {
    height: 56,
    borderRadius: 14,
    fontSize: 20,
    fontWeight: 600,
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    color: '#0f172a',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s ease',
  },
  keyEmpty: { background: 'transparent', border: 'none', cursor: 'default' },
  keyBack: { background: '#fff7ed', color: '#c2410c', borderColor: '#fed7aa' },
};
